<script setup lang="ts">
import { computed, ref } from 'vue'

import FlatButton from '@/components/atoms/FlatButton.vue'
import {
  useSettingsPageChatMessageTemplateViewQuery,
  useSettingsPageDeleteChatMessageTemplateMutation,
} from './SettingsPageChatMessageTemplateView.generated'

import SettingsPagePanel from '@/pages/SettingsPage/SettingsPagePanel.vue'
import SettingsPagePanelHeader from '@/pages/SettingsPage/SettingsPagePanelHeader.vue'
import DolphinChip from '@/components/atoms/DolphinChip.vue'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import { captureException } from '@sentry/vue'
import { useAlert } from '@/libs/alertPortal'
import { useRoute } from 'vue-router'

const route = useRoute('chatMessageTemplate')

const { result, refetch } = useSettingsPageChatMessageTemplateViewQuery({
  fetchPolicy: 'no-cache',
})

const templates = computed(
  () => result.value?.viewer.chatMessageTemplates ?? []
)

const templatesWithCheck = computed(() =>
  templates.value.map(setting => ({
    ...setting,
    checked: checkedTemplateGuids.value.includes(setting.guid),
  }))
)

const checkedTemplateGuids = ref<string[]>([])

const handleChangeChecked = (index: number, checked: boolean) => {
  if (checked) {
    checkedTemplateGuids.value = Array.from(
      new Set([...checkedTemplateGuids.value, templates.value[index].guid])
    )
    return
  }
  checkedTemplateGuids.value = checkedTemplateGuids.value.filter(
    guid => guid !== templates.value[index].guid
  )
}

const extraTypeString = (selections: string[]): string => {
  if (selections.length === 0) {
    return '本文のみ'
  } else {
    return '選択肢付き'
  }
}

const extraTypeChipSelectorName = (selections: string[]): string => {
  if (selections.length === 0) {
    return 'extraChip--none'
  } else {
    return 'extraChip--question'
  }
}

const alert = useAlert()
const { mutate } = useSettingsPageDeleteChatMessageTemplateMutation({})
const deleteSubmitting = ref(false)

const handleDelete = async () => {
  if (deleteSubmitting.value) {
    return
  }

  if (!window.confirm('選択したテンプレートを削除しますか？')) {
    return
  }

  deleteSubmitting.value = true
  try {
    for (const guid of checkedTemplateGuids.value) {
      await mutate({ guid: guid })
    }
    alert.success('削除しました')
  } catch (e) {
    alert.error('削除に失敗しました、もう一度お試しください')
    console.error(e)
    captureException(e)
    return
  } finally {
    deleteSubmitting.value = false
    checkedTemplateGuids.value = [] // 削除完了後にチェックされたIDリストをクリア
    refetch()
  }
}

const isDeleteDisabled = computed(() => {
  return (
    deleteSubmitting.value ||
    templatesWithCheck.value.every(value => !value.checked)
  )
})
</script>

<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>チャットメッセージテンプレート</template>
      <template #right>
        <RouterLink
          :to="{
            name: 'chatMessageTemplateNew',
          }"
        >
          <FlatButton>新しいテンプレートを作成</FlatButton>
        </RouterLink>
      </template>
    </SettingsPagePanelHeader>
    <div :class="$style.checkboxSection">
      <FlatButton
        @click="handleDelete"
        :disabled="isDeleteDisabled"
        variant="caution"
        >削除</FlatButton
      >
    </div>
    <ul :class="$style.list">
      <li
        v-for="(template, index) in templatesWithCheck"
        :key="template.guid"
        :class="$style.list__item"
      >
        <DolphinCheckbox
          :class="$style.list__checkbox"
          :modelValue="template.checked"
          @update:model-value="
            (checked: boolean) => handleChangeChecked(index, checked)
          "
        />
        <router-link
          :to="{
            name: 'chatMessageTemplateDetail',
            params: {
              entranceGUID: route.params.entranceGUID,
              chatMessageTemplateGuid: template.guid,
            },
          }"
          :class="$style['listItem']"
        >
          <div>
            <p :class="[$style.keyword__text, $style.text]">
              {{ template.label }}
            </p>
            <p :class="[$style.keyword__text, $style.content]">
              {{ template.body.slice(0, 30) }}
            </p>
          </div>
          <DolphinChip
            :text="extraTypeString(template.selections)"
            :class="[
              $style.extraChip,
              $style[extraTypeChipSelectorName(template.selections)],
            ]"
          />
        </router-link>
      </li>
    </ul>
  </SettingsPagePanel>
</template>

<style lang="scss" module>
.checkboxSection {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 0 0 8px;
  width: 100%;
  justify-content: flex-end;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:nth-child(n + 2) {
    margin-top: 12px;
  }

  &__item {
    &:hover {
      background-color: $color-surface-accent-primary-light;
    }
  }

  &__checkbox {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 16px;
    pointer-events: auto;
  }
}

.listItem {
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-v2-base;
  display: flex;
  align-items: center;
  padding: 16px 16px 16px 56px;
  justify-content: space-between;

  &:hover {
    text-decoration: none;
  }
}

.text {
  color: $color-okaneko-blue;
  font-weight: bold;
}

.content {
  margin-top: 10px;
  font-size: 0.9rem;
}

.extraChip {
  font-weight: 700;
  color: $color-text-and-object-high-emphasis-inverse;

  &--none {
    background-color: $color-label-orange;
  }

  &--question {
    background-color: $color-label-yellow;
  }

  &--bookingRequest {
    background-color: $color-label-water;
  }
}
</style>
